import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Spin, Table, Tag, Space } from 'antd'
import type { Key } from 'antd/es/table/interface'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  LockOutlined,
  FlagOutlined,
} from '@ant-design/icons'
import { useBulkUpdateAssetReviewMutation, useComplianceAssets } from '@/features/compliance/hooks'
import { AssetReviewStatus, type ComplianceAssetReviewItem } from '@/features/compliance/types'
import { formatComplianceAmount, getComplianceRowStatus, type ComplianceRowStatus } from '@/features/compliance/utils'
import { useToast } from '@/contexts/ToastContext'
import { AccountDetailDrawer } from './AccountDetailDrawer'

type ReviewResultsStepProps = {
  clientId: string
  jobId: string
  onNext: () => void
  onBack?: () => void
  onAddFlaggedWorkpapers?: () => void
  disableReupload?: boolean
  onJobStatusChange?: (jobStatus?: string | null) => void
}

export type RowStatus = ComplianceRowStatus

type RowWithStatus = ComplianceAssetReviewItem & { rowStatus: RowStatus }

export function ReviewResultsStep({
  clientId,
  jobId,
  onNext,
  onBack,
  onAddFlaggedWorkpapers,
  disableReupload = false,
  onJobStatusChange,
}: ReviewResultsStepProps) {
  const [activeTab, setActiveTab] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [selectedItem, setSelectedItem] = useState<RowWithStatus | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [bulkNotesOpen, setBulkNotesOpen] = useState(false)
  const [bulkNotesValue, setBulkNotesValue] = useState('')

  const { addToast } = useToast()
  const assetsQuery = useComplianceAssets(clientId, jobId)
  const { refetch: refetchAssets } = assetsQuery
  const bulkUpdateReviewMutation = useBulkUpdateAssetReviewMutation(clientId, jobId)
  const rows = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data])

  useEffect(() => {
    void refetchAssets()
  }, [refetchAssets])

  const rowsWithStatus = useMemo(() => {
    return rows.map((r) => ({ ...r, rowStatus: getComplianceRowStatus(r) as RowStatus }))
  }, [rows])

  const summary = useMemo(() => {
    let match = 0
    let mismatch = 0
    let missing = 0
    rowsWithStatus.forEach((r) => {
      if (r.rowStatus === 'match') match++
      else if (r.rowStatus === 'mismatch') mismatch++
      else missing++
    })
    return { total: rows.length, match, mismatch, missing }
  }, [rowsWithStatus, rows.length])

  const filteredRows = useMemo(() => {
    let list = rowsWithStatus
    if (activeTab === 'match') list = list.filter((r) => r.rowStatus === 'match')
    else if (activeTab === 'mismatch') list = list.filter((r) => r.rowStatus === 'mismatch')
    else if (activeTab === 'missing') list = list.filter((r) => r.rowStatus === 'missing')
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (r) =>
          (r.assetName ?? '').toLowerCase().includes(q) ||
          (r.amount ?? '').toLowerCase().includes(q) ||
          (r.workpaperAmount ?? '').toLowerCase().includes(q) ||
          (r.matchedWorkpaperFileName ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [rowsWithStatus, activeTab, search])

  const selectedRows = useMemo(
    () => filteredRows.filter((r) => selectedRowKeys.includes(r.complianceAssetId)),
    [filteredRows, selectedRowKeys]
  )

  const handleBulkUpdate = useCallback(
    async (status: AssetReviewStatus) => {
      if (selectedRows.length === 0) return
      try {
        const result = await bulkUpdateReviewMutation.mutateAsync({
          complianceAssetIds: selectedRows.map((row) => row.complianceAssetId),
          status,
        })
        const success = result.updatedCount ?? 0
        const failed = result.failedCount ?? 0
        void assetsQuery.refetch()
        onJobStatusChange?.(result.jobStatus)
        setSelectedRowKeys([])
        if (failed === 0) addToast(`Updated ${success} item${success !== 1 ? 's' : ''}`, 'success')
        else addToast(`Updated ${success}; ${failed} failed`, 'error')
      } catch {
        addToast('Bulk update failed', 'error')
      }
    },
    [selectedRows, bulkUpdateReviewMutation, assetsQuery, addToast]
  )

  const handleBulkNotes = useCallback(async () => {
    if (selectedRows.length === 0) return
    try {
      const result = await bulkUpdateReviewMutation.mutateAsync({
        complianceAssetIds: selectedRows.map((row) => row.complianceAssetId),
        notes: bulkNotesValue.trim() || null,
      })
      const success = result.updatedCount ?? 0
      const failed = result.failedCount ?? 0
      void assetsQuery.refetch()
      onJobStatusChange?.(result.jobStatus)
      setSelectedRowKeys([])
      setBulkNotesOpen(false)
      setBulkNotesValue('')
      if (failed === 0) addToast(`Added notes to ${success} item${success !== 1 ? 's' : ''}`, 'success')
      else addToast(`Updated ${success}; ${failed} failed`, 'error')
    } catch {
      addToast('Bulk notes update failed', 'error')
    }
  }, [selectedRows, bulkNotesValue, bulkUpdateReviewMutation, assetsQuery, addToast])

  const handleOpenBulkNotes = useCallback(() => {
    const existingNotes = Array.from(
      new Set(
        selectedRows
          .map((row) => row.reviewNotes?.trim() ?? '')
          .filter((note) => note.length > 0)
      )
    )

    setBulkNotesValue(existingNotes.length === 1 ? (existingNotes[0] ?? '') : '')
    setBulkNotesOpen(true)
  }, [selectedRows])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [activeTab, search, rowsWithStatus])

  const openDrawerForRow = useCallback((record: RowWithStatus) => {
    setSelectedItem(record)
    setDrawerOpen(true)
  }, [])

  const columns = [
    {
      title: 'ACCOUNT',
      dataIndex: 'assetName',
      key: 'account',
      render: (_: unknown, r: RowWithStatus) => (
        <button
          type="button"
          onClick={() => openDrawerForRow(r)}
          className="feature-link font-medium text-left hover:underline cursor-pointer"
        >
          {r.assetName || '—'}
        </button>
      ),
    },
    {
      title: 'TB AMOUNT',
      dataIndex: 'amount',
      key: 'tbAmount',
      render: (_: unknown, r: ComplianceAssetReviewItem) => formatComplianceAmount(r.amount),
    },
    {
      title: 'WP AMOUNT',
      dataIndex: 'workpaperAmount',
      key: 'wpAmount',
      render: (_: unknown, r: ComplianceAssetReviewItem) => formatComplianceAmount(r.workpaperAmount),
    },
    {
      title: 'MATCHED WORKPAPER',
      dataIndex: 'matchedWorkpaperFileName',
      key: 'matchedWorkpaper',
      render: (_: unknown, r: ComplianceAssetReviewItem) => (
        <div className="max-w-[320px] whitespace-normal break-words">
          {r.matchedWorkpaperFileName?.trim() ? r.matchedWorkpaperFileName : 'No workpaper found'}
        </div>
      ),
    },
    {
      title: 'SHEETS',
      dataIndex: 'sheetName',
      key: 'sheets',
      render: (_: unknown, r: ComplianceAssetReviewItem) => {
        const legacySheets = Array.isArray(r.matchedWorkpaperSheets) ? r.matchedWorkpaperSheets : []
        const hasLegacySheets = legacySheets.length > 0
        if (hasLegacySheets) {
          return <div className="max-w-[220px] whitespace-normal break-words">{legacySheets.join(', ')}</div>
        }
        const singleSheet = r.sheetName?.trim()
        return singleSheet ? <div className="max-w-[220px] whitespace-normal break-words">{singleSheet}</div> : '—'
      },
    },
    {
      title: 'TYPE',
      dataIndex: 'class',
      key: 'type',
      render: (_: unknown, r: ComplianceAssetReviewItem) => (
        <div className="max-w-[180px] whitespace-normal break-words">{r.class?.trim() || '—'}</div>
      ),
    },
    {
      title: 'REVIEWER NOTES',
      dataIndex: 'reviewNotes',
      key: 'reviewerNotes',
      render: (_: unknown, r: ComplianceAssetReviewItem) => (
        <div className="max-w-[320px] whitespace-normal break-words">{r.reviewNotes?.trim() ? r.reviewNotes : '—'}</div>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      dataIndex: 'rowStatus',
      render: (_: unknown, r: RowWithStatus) => {
        const reviewStatus = r.reviewStatus
        let statusBadge
        if (r.rowStatus === 'match')
          statusBadge = (
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircleOutlined /> Match
            </span>
          )
        else if (r.rowStatus === 'mismatch')
          statusBadge = (
            <span className="inline-flex items-center gap-1 text-red-700">
              <CloseCircleOutlined /> Mismatch
            </span>
          )
        else
          statusBadge = (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <ExclamationCircleOutlined /> Missing
            </span>
          )

        return (
          <Space direction="vertical" size={4}>
            {statusBadge}
            {reviewStatus === AssetReviewStatus.Locked && (
              <Tag icon={<LockOutlined />} color="default" className="text-xs">
                Locked
              </Tag>
            )}
            {reviewStatus === AssetReviewStatus.NeedsReview && (
              <Tag icon={<FlagOutlined />} color="orange" className="text-xs">
                Needs Review
              </Tag>
            )}
            {reviewStatus === AssetReviewStatus.Reviewed && (
              <Tag icon={<CheckCircleOutlined />} color="green" className="text-xs">
                Reviewed
              </Tag>
            )}
          </Space>
        )
      },
    },
  ]

  const getRowClassName = (r: RowWithStatus) => {
    const baseClass = r.rowStatus === 'match' ? 'bg-green-50' : r.rowStatus === 'mismatch' ? 'bg-red-50' : 'bg-amber-50'
    const reviewStatus = r.reviewStatus
    if (reviewStatus === AssetReviewStatus.Locked) return `${baseClass} opacity-60`
    if (reviewStatus === AssetReviewStatus.NeedsReview) return `${baseClass} border-l-4 border-orange-500`
    if (reviewStatus === AssetReviewStatus.Reviewed) return `${baseClass} border-l-4 border-green-500`
    return baseClass
  }

  if (assetsQuery.isLoading && !assetsQuery.data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50/50">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-2xl font-semibold text-slate-900">{summary.total}</div>
          <div className="text-sm text-slate-600">Total Line Items</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-2xl font-semibold text-green-700">{summary.match}</div>
          <div className="text-sm text-slate-600">Matching</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-2xl font-semibold text-red-700">{summary.mismatch}</div>
          <div className="text-sm text-slate-600">Mismatch</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-2xl font-semibold text-amber-700">{summary.missing}</div>
          <div className="text-sm text-slate-600">No Workpaper</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          {[
            { key: 'all', label: `All (${summary.total})` },
            { key: 'match', label: `Match (${summary.match})` },
            { key: 'mismatch', label: `Mismatch (${summary.mismatch})` },
            { key: 'missing', label: `Missing (${summary.missing})` },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'feature-button-solid text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Input.Search
          placeholder="Search line items..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        {assetsQuery.isFetching && (
          <div
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-600 shadow-sm"
            role="status"
            aria-live="polite"
          >
            <Spin size="small" indicator={<LoadingOutlined spin />} />
            Refreshing assets...
          </div>
        )}
      </div>

      {selectedRowKeys.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3" role="toolbar" aria-label="Bulk actions">
          <span className="text-sm font-medium text-slate-700">
            {selectedRowKeys.length} selected
          </span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => void handleBulkUpdate(AssetReviewStatus.Reviewed)}
            loading={bulkUpdateReviewMutation.isPending}
            disabled={bulkUpdateReviewMutation.isPending}
            className="bg-teal-600 hover:bg-teal-700 text-white border-teal-600"
          >
            Mark as Reviewed
          </Button>
          <Button
            size="small"
            icon={<FlagOutlined />}
            onClick={() => void handleBulkUpdate(AssetReviewStatus.NeedsReview)}
            loading={bulkUpdateReviewMutation.isPending}
            disabled={bulkUpdateReviewMutation.isPending}
            danger
          >
            Flag for Follow-up
          </Button>
          <Button
            size="small"
            icon={<LockOutlined />}
            onClick={() => void handleBulkUpdate(AssetReviewStatus.Locked)}
            loading={bulkUpdateReviewMutation.isPending}
            disabled={bulkUpdateReviewMutation.isPending}
            className="bg-slate-500 hover:bg-slate-600 text-white border-slate-500"
          >
            Lock Item
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={handleOpenBulkNotes}
            disabled={bulkUpdateReviewMutation.isPending}
          >
            Add Notes
          </Button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
        <Table
          rowKey="complianceAssetId"
          columns={columns}
          dataSource={filteredRows}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredRows.length,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (current, pageSize) => setPagination({ current, pageSize }),
          }}
          rowClassName={(r) => getRowClassName(r as RowWithStatus)}
          scroll={{ x: 800 }}
          rowSelection={{
            selectedRowKeys,
            type: 'checkbox',
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
        />
      </div>

      <AccountDetailDrawer
        clientId={clientId}
        jobId={jobId}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSelectedItem(null)
        }}
        item={selectedItem}
        reviewState={
          selectedItem
            ? {
                status: selectedItem.reviewStatus || undefined,
                notes: selectedItem.reviewNotes || undefined,
              }
            : undefined
        }
        onReviewUpdated={async (jobStatus) => {
          onJobStatusChange?.(jobStatus)
          await assetsQuery.refetch()
        }}
      />

      <Modal
        title={`Add Reviewer Notes (${selectedRowKeys.length} item${selectedRowKeys.length !== 1 ? 's' : ''})`}
        open={bulkNotesOpen}
        onCancel={() => {
          setBulkNotesOpen(false)
          setBulkNotesValue('')
        }}
        onOk={() => void handleBulkNotes()}
        okText="Apply Notes"
        confirmLoading={bulkUpdateReviewMutation.isPending}
        destroyOnClose
      >
        <p className="mb-3 text-sm text-slate-600">
          This note will be applied to all {selectedRowKeys.length} selected item{selectedRowKeys.length !== 1 ? 's' : ''}.
        </p>
        <div className="pb-5">
          <Input.TextArea
            rows={4}
            placeholder="Enter reviewer note..."
            value={bulkNotesValue}
            onChange={(e) => setBulkNotesValue(e.target.value)}
            maxLength={1000}
            showCount
            autoFocus
          />
        </div>
      </Modal>

      <div className="mt-6 flex shrink-0 justify-between items-center">
        {onBack && (
          <Button size="large" onClick={onBack}>
            ← Back to Upload
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {rows.some((r) => r.reviewStatus === AssetReviewStatus.NeedsReview) &&
            onBack &&
            !disableReupload && (
            <Button size="large" onClick={onAddFlaggedWorkpapers ?? onBack}>
              Add Workpapers for Flagged Items
            </Button>
            )}
          <Button type="primary" size="large" onClick={onNext}>
            Finalize & Export →
          </Button>
        </div>
      </div>
    </div>
  )
}
